import React, { useEffect, useMemo, useState } from "react";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import Modal from "../../components/Modal";
import { CONVEX_CONFIGURED, PROD_CONVEX_URL } from "../../utility/backendConfig";
import { useMsalConvexAuth } from "../../convex/useMsalConvexAuth";
import JevViewer from "./JevViewer";
import JevKnowledge from "./JevKnowledge";
import JevFav2 from "./JevFav2";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Jev window (2026-10-07) — the header 🧠 button opens this full-screen modal.
//
// Always reads the PROD Convex box (bright-bear-880), whatever env the app
// points at: the blackout-dates Lambda's real FAV calls land in PROD Mongo and
// reach only that box (Scheduled_AuditForwardFeed). UAT / developerMode test calls
// are written to UAT Mongo and are deliberately NOT shown here. Same authed per-box client
// pattern as Compass; works in convexOnly AND mongoOnly (it never touches the
// user's backend mode). Gated client-side by canAccessJev and server-side by
// the "jev" token check in convex/scheduler/jev.ts.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// A refused or failed Convex query throws during render; with no boundary that
// unmounts the WHOLE app (#596 review 1). Contain it to the window.
class JevErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (!this.state.error) return this.props.children;
    // Retry rebuilds the Convex client too (onRetry bumps its key), so a sign-in
    // token that wasn't ready on the first request is fetched again (#596 reviews 2 + 6).
    return (
      <div className="p-6 text-skin-link">
        <div>Couldn't load Jev.</div>
        <div className="text-xs mt-2">
          If this keeps happening: access is checked against the PRODUCTION user record, so the "jev" Tab Access token must be granted there.
        </div>
        <div className="text-xs mt-2 opacity-75">{String(this.state.error?.message || this.state.error)}</div>
        <button
          type="button"
          className="mt-3 px-3 py-1 border-2 border-skin-base rounded-sm text-xs"
          onClick={() => {
            this.setState({ error: null });
            if (this.props.onRetry) this.props.onRetry();
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}

function JevModal({ setOpenJevModal }) {
  const close = () => setOpenJevModal(false);
  const { fetchAccessToken } = useMsalConvexAuth();
  const [attempt, setAttempt] = useState(0);
  const [tab, setTab] = useState("calls"); // "calls" | "knowledge" (PRED-016 part 4)
  const prodClient = useMemo(() => {
    if (!CONVEX_CONFIGURED) return null;
    const c = new ConvexReactClient(PROD_CONVEX_URL);
    c.setAuth(fetchAccessToken);
    return c;
    // `attempt` is a deliberate dependency: Try again needs a NEW client.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAccessToken, attempt]);
  useEffect(() => () => { if (prodClient) prodClient.close(); }, [prodClient]);

  return (
    <Modal onClose={close} disableCloseOnClickOutSide>
      <Modal.Header>
        <span className="flex items-center gap-2">
          <span aria-hidden="true">🧠</span> Jev
          <span className="text-xs font-normal text-skin-link">live · PRODUCTION</span>
        </span>
      </Modal.Header>
      <Modal.Content>
        <div style={{ width: "92vw", minHeight: "70vh" }}>
          {prodClient ? (
            <>
              {/* The tabs sit OUTSIDE the error boundary, and the boundary is keyed by tab:
                  a failure on one tab never locks you out of the other (#623 review 4). */}
              <div className="flex gap-2 mb-2" role="tablist">
                  {[["calls", "Calls"], ["knowledge", "What Jev knows"], ["fav2", "FAV 2.0"]].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={tab === key}
                      className={`px-3 py-1 border-2 rounded-sm text-xs ${tab === key ? "border-skin-base font-bold" : "border-transparent"}`}
                      onClick={() => setTab(key)}
                    >
                      {label}
                    </button>
                  ))}
              </div>
              <JevErrorBoundary key={tab} onRetry={() => setAttempt((n) => n + 1)}>
                <ConvexProvider client={prodClient}>
                  {tab === "calls" ? <JevViewer /> : tab === "knowledge" ? <JevKnowledge /> : <JevFav2 />}
                </ConvexProvider>
              </JevErrorBoundary>
            </>
          ) : (
            <div className="p-6 text-skin-link">Convex is not configured in this build.</div>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}

export default JevModal;
